import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import Editor, { type Monaco } from '@monaco-editor/react';
import { useRef } from 'react';
import { NODE_CATALOG, NODE_CATEGORIES } from '../../lib/architectureNodeCatalog';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { useThemeStore } from '../../store/useThemeStore';

const SCHEMA_URI = 'hyphen-db-studio://architecture-diagram-schema.json';

/** JSON Schema describing ArchitectureDiagram, so Monaco's built-in JSON language
 * service gives real-time validation + autocomplete for kinds/categories/directions —
 * far richer than the hand-rolled tokenizer the DBML editor needs for its own DSL. */
function buildJsonSchema() {
  const kinds = Array.from(new Set(NODE_CATALOG.map((entry) => entry.kind)));
  return {
    uri: SCHEMA_URI,
    fileMatch: ['*'],
    schema: {
      type: 'object',
      additionalProperties: false,
      required: ['nodes', 'edges'],
      properties: {
        nodes: {
          type: 'array',
          items: {
            type: 'object',
            required: ['id', 'kind', 'category', 'label'],
            properties: {
              id: { type: 'string' },
              kind: { type: 'string', enum: kinds },
              category: { type: 'string', enum: NODE_CATEGORIES },
              label: { type: 'string' },
              note: { type: 'string' },
              mode: { type: 'string', enum: ['component', 'group'] },
              parentId: { type: ['string', 'null'] },
              color: { type: 'string' },
              position: {
                type: 'object',
                required: ['x', 'y'],
                properties: { x: { type: 'number' }, y: { type: 'number' } },
              },
            },
          },
        },
        edges: {
          type: 'array',
          items: {
            type: 'object',
            required: ['id', 'fromNodeId', 'toNodeId', 'direction', 'animated'],
            properties: {
              id: { type: 'string' },
              fromNodeId: { type: 'string' },
              toNodeId: { type: 'string' },
              fromHandle: { type: ['string', 'null'], enum: ['top', 'right', 'bottom', 'left', null] },
              toHandle: { type: ['string', 'null'], enum: ['top', 'right', 'bottom', 'left', null] },
              label: { type: 'string' },
              direction: { type: 'string', enum: ['forward', 'backward', 'bidirectional', 'none'] },
              animated: { type: 'boolean' },
              speed: { type: 'number' },
            },
          },
        },
      },
    },
  };
}

let schemaRegistered = false;

/** Registered once via `beforeMount` (not on every mount/remount as the JSON Source tab
 * is switched to and from) — re-registering the same schema repeatedly cancels the JSON
 * worker's in-flight validation each time, which otherwise surfaces as spurious
 * "Canceled" page errors. */
function registerJsonSchema(monaco: Monaco) {
  if (schemaRegistered) return;
  schemaRegistered = true;
  monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
    validate: true,
    schemas: [buildJsonSchema()],
  });
}

export default function ArchitectureJsonEditor() {
  const readOnly = useDiagramViewStore((s) => s.readOnly);
  const jsonSource = useArchitectureStore((s) => s.jsonSource);
  const parseErrors = useArchitectureStore((s) => s.parseErrors);
  const setJsonSource = useArchitectureStore((s) => s.setJsonSource);
  const theme = useThemeStore((s) => s.theme);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  function handleChange(value: string | undefined) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const text = value ?? '';
    debounceRef.current = setTimeout(() => setJsonSource(text), 300);
  }

  return (
    <div className="flex h-full flex-col">
      <Editor
        height="100%"
        defaultLanguage="json"
        language="json"
        theme={theme === 'dark' ? 'vs-dark' : 'vs'}
        value={jsonSource}
        onChange={handleChange}
        beforeMount={registerJsonSchema}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 13,
          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          tabSize: 2,
        }}
      />
      {parseErrors.length > 0 && (
        <div className="max-h-28 overflow-y-auto border-t border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
          {parseErrors.map((err, i) => (
            <div key={i}>{err.message}</div>
          ))}
        </div>
      )}
    </div>
  );
}
